import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Loader2,
  KeyRound,
  Unlock,
  XCircle,
  Ban,
  FileText,
  Database,
  Palette,
  ClipboardList,
  Rocket,
  Wrench,
  TestTube,
  CheckCircle2,
  Bug,
  Inbox,
  UserCheck,
  Hourglass,
  Undo2,
  PlayCircle,
  AtSign,
} from "lucide-react";
import { request } from "../../utils/api";
import { toast } from "react-toastify";
import { ROUTES, STORAGE_KEYS } from "../../utils/constants";
import { GlobalContext } from "../../../app/providers/GlobalProvider";
import { useNotificationSummary } from "../../notifications/NotificationSummaryProvider";
import {
  initAudioContext,
  playPanelOpenChime,
  playMarkReadChime,
  playMarkAllChime,
} from "../../utils/audioUtils";

/**
 * Notification type -> Lucide glyph.
 *
 * This used to be fourteen emoji. Emoji never ship in Imago product chrome:
 * they render differently on every platform, ignore currentColor, and sit on
 * their own baseline. Icons here are Lucide at 2px stroke, coloured by the row.
 */
const NOTIFICATION_ICONS = {
  ACCESS_REQUEST: KeyRound,
  ACCESS_GRANTED: Unlock,
  ACCESS_REJECTED: XCircle,
  ACCESS_REVOKED: Ban,
  PRD_READY: FileText,
  DB_SCHEMA_READY: Database,
  DESIGN_COMPLETE: Palette,
  API_CONTRACT_READY: ClipboardList,
  MOCK_API_DEPLOYED: Rocket,
  MOCK_API_ENRICHED: Wrench,
  TEST_CASES_READY: TestTube,
  STAGE_COMPLETED: CheckCircle2,
  BUG_REPORTED: Bug,
  // Workflow-stream types (from /api/workflows/notifications)
  STAGE_ASSIGNED: UserCheck,
  APPROVAL_REQUESTED: Hourglass,
  STAGE_APPROVED: CheckCircle2,
  REVISION_REQUESTED: Undo2,
  STAGE_READY: PlayCircle,
  MENTION: AtSign,
};

// Human titles for workflow-stream notifications, which carry no title field.
const WORKFLOW_TYPE_TITLES = {
  STAGE_ASSIGNED: "Stage assigned to you",
  APPROVAL_REQUESTED: "Approval requested",
  STAGE_APPROVED: "Stage approved",
  REVISION_REQUESTED: "Revision requested",
  STAGE_READY: "Stage ready",
  MENTION: "You were mentioned",
};

const workflowTypeTitle = (type) =>
  WORKFLOW_TYPE_TITLES[type] ||
  (type ? type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, " ") : "Workflow update");

const NotificationPanel = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { fetchProjects, fetchPendingRequests, setProjects } = useContext(GlobalContext);
  const { refresh: refreshSummary } = useNotificationSummary();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingProjectId, setLoadingProjectId] = useState(null);
  const [filter, setFilter] = useState("all"); // 'all' or 'unread'
  const panelRef = useRef(null);
  const audioInitializedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, filter]);

  useEffect(() => {
    if (isOpen) {
      if (!audioInitializedRef.current) {
        initAudioContext();
        audioInitializedRef.current = true;
      }
      playPanelOpenChime();
    }
  }, [isOpen]);

  useEffect(() => {
    // Close panel when clicking outside
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // One panel, two streams. Platform and workflow notifications live in
  // different tables with different mark-read endpoints, so they are fetched
  // separately, normalised to the platform row shape, and merged by time.
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const queryParam = filter === "unread" ? "?unreadOnly=true" : "";
      const [platformRes, workflowRes] = await Promise.allSettled([
        request(`/api/notifications${queryParam}`, "GET"),
        request(`/api/workflows/notifications${queryParam}`, "GET"),
      ]);

      let merged = [];
      let unread = 0;

      if (platformRes.status === "fulfilled" && platformRes.value?.success) {
        const rows = (platformRes.value.data || []).map((n) => ({ ...n, source: "platform" }));
        merged = merged.concat(rows);
        unread += platformRes.value.unread_count || 0;
      }

      if (workflowRes.status === "fulfilled" && workflowRes.value) {
        const rows = (workflowRes.value.notifications || []).map((n) => ({
          source: "workflow",
          id: n.id,
          type: n.notificationType,
          title: workflowTypeTitle(n.notificationType),
          message: n.message,
          is_read: !!n.isRead,
          created_at: n.createdAt,
          workflowId: n.workflowId,
          stageType: n.stageType,
        }));
        merged = merged.concat(rows);
        unread += rows.filter((n) => !n.is_read).length;
      }

      merged.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setNotifications(merged);
      setUnreadCount(unread);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  // Listen for new notifications pushed via websocket and prepend to list
  useEffect(() => {
    const handler = async (e) => {
      const payload = e.detail;
      if (!payload) return;

      // Prepend new notification to list (websocket pushes are platform-stream)
      setNotifications((prev) => {
        // Prevent duplicates
        const exists = prev?.some(n => n.source === "platform" && n.id === payload.id);
        if (exists) return prev;
        return [{ ...payload, source: "platform" }, ...(prev || [])];
      });
      setUnreadCount((prev) => (prev || 0) + 1);

      // If ACCESS_GRANTED notification, show toast prompting user to click "Go to Project"
      if (payload.type === "ACCESS_GRANTED") {
        toast.success('Access granted! Click "Go to Project" in notifications to access it.', {
          autoClose: 5000,
        });
      }

      // If ACCESS_REQUEST notification, refresh pending requests for owner
      if (payload.type === "ACCESS_REQUEST") {
        try {
          await fetchPendingRequests();
        } catch (error) {
          console.error("Failed to refresh pending requests:", error);
        }
      }
    };
    window.addEventListener("new-notification", handler);
    return () => window.removeEventListener("new-notification", handler);
  }, [fetchPendingRequests]);

  const markAsRead = async (notification) => {
    const notificationId = notification.id;
    try {
      // Each stream keeps its own mark-read endpoint.
      if (notification.source === "workflow") {
        await request(`/api/workflows/notifications/${notificationId}/read`, "PUT");
      } else {
        const data = await request(`/api/notifications/${notificationId}/read`, "PUT");
        if (!data.success) return;
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.source === notification.source && notif.id === notificationId
            ? { ...notif, is_read: true }
            : notif
        )
      );
      const newCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newCount);
      // Dispatch event for NotificationBell to sync its count
      window.dispatchEvent(new CustomEvent('notification-count-updated', { detail: { unreadCount: newCount } }));
      // Keep the shared summary (navbar badge) in step instead of waiting for the next poll
      refreshSummary();
      // play small confirmation tone
      playMarkReadChime(notification.type);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await Promise.allSettled([
        request("/api/notifications/read-all", "PUT"),
        request("/api/workflows/notifications/read-all", "PUT"),
      ]);

      // Update all notifications to read
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, is_read: true }))
      );
      setUnreadCount(0);
      // Dispatch event for NotificationBell to sync its count
      window.dispatchEvent(new CustomEvent('notification-count-updated', { detail: { unreadCount: 0 } }));
      refreshSummary();
      toast.success("All notifications marked as read");
      // play confirmation tone
      playMarkAllChime();
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Failed to mark all as read");
    }
  };

  const handleGoToProject = async (notification) => {
    try {
      if (!notification.related_project_id) {
        toast.error("Project information not available");
        return;
      }

      // Set loading state for this specific notification
      setLoadingProjectId(notification.id);

      // Mark notification as read
      if (!notification.is_read) {
        await markAsRead(notification);
      }

      // Fetch all projects (includes the newly granted one with user's access context)
      const response = await request("/api/project/v2/getAllProjects", "POST", { payload: {} });

      if (response.success && response.data?.projects) {
        const allAccessibleProjects = response.data.projects;
        const targetProject = allAccessibleProjects.find(p => p.id === notification.related_project_id);

        if (targetProject) {
          // Get existing projects from sessionStorage to preserve UI state (isOpen, editing, etc.)
          const cachedProjects = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.PROJECTS) || "[]");

          // Create a map of existing projects by ID for quick lookup of UI state
          const existingProjectsMap = new Map(cachedProjects.map(p => [p.id, p]));

          // Format ALL projects from API response, preserving UI state where it exists
          const updatedProjects = allAccessibleProjects.map((project) => {
            const existingProject = existingProjectsMap.get(project.id);

            return {
              ...project,
              isOpen: existingProject?.isOpen || false,
              editing: existingProject?.editing || false,
              collections: project.collections?.map((collection) => {
                const existingCollection = existingProject?.collections?.find(c => c.id === collection.id);

                return {
                  ...collection,
                  isOpen: existingCollection?.isOpen || false,
                  editing: existingCollection?.editing || false,
                  folders: collection.folders?.map((folder) => {
                    const existingFolder = existingCollection?.folders?.find(f => f.id === folder.id);

                    return {
                      ...folder,
                      isOpen: existingFolder?.isOpen || false,
                      editing: existingFolder?.editing || false,
                      sub_folders: folder.sub_folders?.map((sub_folder) => {
                        const existingSubFolder = existingFolder?.sub_folders?.find(sf => sf.id === sub_folder.id);

                        return {
                          ...sub_folder,
                          isOpen: existingSubFolder?.isOpen || false,
                          editing: existingSubFolder?.editing || false,
                        };
                      }),
                    };
                  }),
                };
              }),
            };
          });


          // Replace entire sessionStorage with fresh API data (preserving UI state)

          // Directly update global state with the complete list
          if (setProjects) {
            setProjects(updatedProjects);
          } else {
            // Fallback: trigger fetchProjects which will read from updated sessionStorage
            await fetchProjects();
          }

          const wasNewlyAdded = !existingProjectsMap.has(targetProject.id);
          if (wasNewlyAdded) {
            toast.success(`Project "${targetProject.name}" added to your workspace`, {
              autoClose: 3000,
            });
          } else {
            toast.info("Project is already in your workspace!");
          }

          // Navigate to Projects page and request it open the specific project
          navigate(ROUTES.INPUTS, { state: { openProjectId: targetProject.id } });
          onClose();
        } else {
          toast.error("Project not found or access was revoked");
        }
      } else {
        toast.error("Failed to load project details");
      }
    } catch (error) {
      console.error("❌ Error loading project:", error);
      toast.error("Failed to load project. Please refresh the page and try again.");
    } finally {
      // Clear loading state
      setLoadingProjectId(null);
    }
  };

  // Map workflow-ish platform notification types to their target routes.
  // The role workspaces these used to point at are gone; everything stage
  // related now lands on the workflow screen itself.
  const WORKFLOW_NOTIFICATION_ROUTES = {
    PRD_READY: ROUTES.WORKFLOW,
    DB_SCHEMA_READY: ROUTES.WORKFLOW,
    DESIGN_COMPLETE: ROUTES.WORKFLOW,
    API_CONTRACT_READY: ROUTES.WORKFLOW,
    MOCK_API_DEPLOYED: ROUTES.WORKFLOW,
    MOCK_API_ENRICHED: ROUTES.WORKFLOW,
    TEST_CASES_READY: ROUTES.WORKFLOW,
    STAGE_COMPLETED: ROUTES.WORKFLOW,
    BUG_REPORTED: ROUTES.WORKFLOW,
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification);
    }

    // Workflow-stream rows deep-link into the workflow they belong to.
    if (notification.source === "workflow") {
      navigate(notification.workflowId ? `/workflow/${notification.workflowId}` : ROUTES.WORKFLOW);
      onClose();
      return;
    }

    // Navigate based on notification type
    if (notification.type === "ACCESS_REQUEST") {
      // Refresh pending requests for owner
      try {
        await fetchPendingRequests();
      } catch (error) {
        console.error("Failed to refresh pending requests:", error);
      }
      // Navigate to dashboard where approval requests are shown
      navigate(ROUTES.DASHBOARD);
      onClose(); // Close the notification panel
    } else if (notification.type === "ACCESS_GRANTED") {
      // Use Go to Project button for better UX - clicking notification shows message
      toast.info('Click "Go to Project" button to access the new project');
    } else if (notification.type === "ACCESS_REJECTED") {
      // Navigate to dashboard
      navigate(ROUTES.DASHBOARD);
      onClose();
    } else if (notification.type === "ACCESS_REVOKED") {
      // Refresh projects to remove the revoked project from the list
      try {
        await fetchProjects();
        toast.info('Your access has been removed. The project is no longer available.');
      } catch (error) {
        console.error("Failed to refresh projects:", error);
      }
      navigate(ROUTES.INPUTS); // Navigate to projects page
      onClose();
    } else if (WORKFLOW_NOTIFICATION_ROUTES[notification.type]) {
      navigate(WORKFLOW_NOTIFICATION_ROUTES[notification.type]);
      onClose();
    }
  };

  const getNotificationIcon = (type) => {
    const Icon = NOTIFICATION_ICONS[type] || Inbox;
    return <Icon className="w-5 h-5" aria-hidden="true" />;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      {/* Overlay */}
      <div className="fixed inset-0 bg-transparent" onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        className="
          fixed top-16 right-4 w-[400px] max-w-[calc(100vw-2rem)]
          max-h-[600px] bg-card rounded-lg border border-border
          shadow-lg flex flex-col overflow-hidden z-[9999]
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted hover:text-foreground hover:bg-background-secondary transition-colors"
            aria-label="Close notifications"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`
                px-3 py-1.5 text-sm rounded-md border transition-colors
                ${filter === "all"
                  ? "bg-primary text-white border-primary"
                  : "bg-transparent text-foreground border-border hover:bg-card-hover"
                }
              `}
            >
              All
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`
                px-3 py-1.5 text-sm rounded-md border transition-colors
                ${filter === "unread"
                  ? "bg-primary text-white border-primary"
                  : "bg-transparent text-foreground border-border hover:bg-card-hover"
                }
              `}
            >
              Unread ({unreadCount})
            </button>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary-hover transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted">
              <p>No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={`notification-${notification.source}-${notification.id}-${notification.created_at}`}
                onClick={() => handleNotificationClick(notification)}
                className={`
                  flex gap-3 px-5 py-4 border-b border-border-secondary cursor-pointer
                  transition-colors duration-base ease-standard relative
                  hover:bg-card-hover
                  ${!notification.is_read ? "bg-primary/5 border-l-2 border-l-primary" : ""}
                `}
                title="Click to view details"
              >
                <div className={`flex-shrink-0 mt-0.5 ${!notification.is_read ? "text-primary" : "text-muted"}`}>
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-foreground truncate">
                      {notification.title}
                    </span>
                    {notification.source === "workflow" && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-label font-medium uppercase tracking-eyebrow bg-primary/10 text-primary">
                        Workflow
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-foreground-secondary mb-1 line-clamp-2">
                    {notification.message}
                  </div>
                  {notification.related_project_name && (
                    <div className="text-xs text-muted mb-1">
                      Project: {notification.related_project_name}
                    </div>
                  )}
                  <div className="text-xs text-muted">
                    {formatTime(notification.created_at)}
                  </div>
                  {notification.type === "ACCESS_GRANTED" && notification.related_project_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGoToProject(notification);
                      }}
                      disabled={loadingProjectId === notification.id}
                      className="
                        mt-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md
                        hover:bg-primary-hover transition-all inline-flex items-center gap-2
                        disabled:opacity-50 disabled:cursor-not-allowed
                      "
                    >
                      {loadingProjectId === notification.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Go to Project"
                      )}
                    </button>
                  )}
                  {WORKFLOW_NOTIFICATION_ROUTES[notification.type] && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!notification.is_read) markAsRead(notification);
                        navigate(WORKFLOW_NOTIFICATION_ROUTES[notification.type]);
                        onClose();
                      }}
                      className="mt-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-hover transition-all"
                    >
                      Go to Stage
                    </button>
                  )}
                </div>
                {!notification.is_read && (
                  <div className="absolute top-1/2 right-3 -translate-y-1/2 w-2 h-2 bg-primary rounded-full" />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;
